/**
 * #1932 (owner 2026-10-30, decisions D1 to D3; plan items S3 and S4): the Google service account
 * an environment PINS for GA4 and Google Search Console. One rule, read by the environment
 * generator (`scripts/environmentFile.ts`, `connectorHub.googleServiceAccount`), the web
 * (`server/config.ts`, the editor's read-only address and the validate gate) and the runner (the
 * job environment and the extract gate).
 *
 * With a pin, the in-environment connector hub validates as that account through its own
 * identity, and every ga4 or gsc job carries it as `GA4_SERVICE_ACCOUNT` / `GSC_SERVICE_ACCOUNT`,
 * so a typed address that differs is refused by the image before any Google call. That is safe only
 * where ONE customer owns the account: a pin is refused on a Skypoint placement target and on
 * targets of differing customers (start-up refusal `google_pin_shared_environment`), and while it is
 * set the environment holds one customer tenant (`GOOGLE_PIN_SINGLE_TENANT`). There is no
 * exception, a Skypoint test environment included (record #2427, decision 6).
 *
 * The address is an identifier, not a secret. Even so no refusal quotes it: a sentence names the
 * setting and the reason only.
 */

import { CANARY_TARGET_CUSTOMER } from "../types/tenants";

/** Skypoint's shared account, which the hub impersonates with no settings. Never skyData's. */
export const SHARED_GOOGLE_SERVICE_ACCOUNT =
  "ga4-reader@gen-lang-client-0202893413.iam.gserviceaccount.com";

/** The web's and the runner's setting. */
export const GOOGLE_PIN_SETTING = "GOOGLE_SERVICE_ACCOUNT_PIN";

/** The variable each pinned connector's job carries. No other connector's job gets one. */
export const GOOGLE_PINNED_CONNECTOR_ENV = {
  ga4: "GA4_SERVICE_ACCOUNT",
  gsc: "GSC_SERVICE_ACCOUNT",
} as const satisfies Record<string, string>;

/** The seed field the editor fills, read-only, from the pin. */
export const GOOGLE_PIN_FIELD_ID = "serviceAccountEmail";

/** 409: the second customer tenant of a pinned environment, and a validate or run beside one. */
export const GOOGLE_PIN_SINGLE_TENANT_CODE = "GOOGLE_PIN_SINGLE_TENANT";

/** The placement target customer that refuses a pin: Skypoint's, as a canary's target is. */
export const GOOGLE_PIN_REFUSED_CUSTOMER = CANARY_TARGET_CUSTOMER;

/**
 * Google's rule: an account id of 6 to 30 lowercase letters, digits and hyphens, starting with a
 * letter, at a project id of the same shape. Anchored, one line, no space.
 */
const SERVICE_ACCOUNT_ADDRESS =
  /^[a-z][a-z0-9-]{4,28}[a-z0-9]@[a-z][a-z0-9-]{4,28}[a-z0-9]\.iam\.gserviceaccount\.com$/;

/** Why `value` cannot be pinned, or null. Never quotes the value. */
export function googleServiceAccountProblem(value: string): string | null {
  if (value.trim().toLowerCase() === SHARED_GOOGLE_SERVICE_ACCOUNT) {
    return "is Skypoint's shared Google service account, which skyData never uses";
  }
  if (!SERVICE_ACCOUNT_ADDRESS.test(value)) {
    return "must be a Google service account address, <name>@<project>.iam.gserviceaccount.com";
  }
  return null;
}

export type GooglePinTargetsProblem = "skypoint_target" | "mixed_customers" | "no_targets";

/**
 * Whether placement targets with these `customer` values may carry a pin: every target one
 * customer, never Skypoint.
 */
export function googlePinTargetsProblem(input: {
  customers: readonly string[];
}): GooglePinTargetsProblem | null {
  if (input.customers.length === 0) return "no_targets";
  if (input.customers.includes(GOOGLE_PIN_REFUSED_CUSTOMER)) return "skypoint_target";
  if (new Set(input.customers).size > 1) return "mixed_customers";
  return null;
}

const TARGET_PROBLEM_WORDS: Record<GooglePinTargetsProblem, string> = {
  skypoint_target: "a placement target's customer is Skypoint",
  mixed_customers: "the placement targets name more than one customer",
  no_targets: "no placement target is registered",
};

/** The start-up refusal sentence (`google_pin_shared_environment`). Names the setting only. */
export function googlePinRefusal(problem: GooglePinTargetsProblem): string {
  return (
    `${GOOGLE_PIN_SETTING} is refused: ${TARGET_PROBLEM_WORDS[problem]}, and one Google service ` +
    "account may serve one customer only (#1932). Remove the pin, or set it in an environment " +
    "that holds that customer alone."
  );
}

export function isGooglePinnedConnector(
  connectorName: string,
): connectorName is keyof typeof GOOGLE_PINNED_CONNECTOR_ENV {
  return Object.hasOwn(GOOGLE_PINNED_CONNECTOR_ENV, connectorName);
}

/** The variable a job of `connectorName` carries for the pin, or nothing. */
export function googleJobEnvFor(
  connectorName: string,
  pin: string | undefined,
): Record<string, string> {
  if (!pin || !isGooglePinnedConnector(connectorName)) return {};
  return { [GOOGLE_PINNED_CONNECTOR_ENV[connectorName]]: pin };
}

/**
 * While a pin is set the environment holds one customer tenant:
 * zero or one is allowed; two or more, or a count that could not be read, is refused.
 */
export function pinAllowsTenantCount(count: number | null): boolean {
  return typeof count === "number" && Number.isInteger(count) && count >= 0 && count <= 1;
}
